import React, {
  useState, useEffect, useRef, useCallback, useMemo, ReactNode,
} from 'react';
import {
  Input, AutoComplete, Flex, InputRef,
} from 'antd';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { LocalStorageService, NavigationService } from 'services';
import { IconSvg } from 'shared/icon';
import { convertDateArrayToJSON, getUrlSearchParams } from 'utils/helpers';
import enumTypes from 'constants/enumTypes';
import { useTimeZone } from 'hooks';
import { setCurrentBrandId } from 'app/userPreferences/slices';
import { makeSelectLocaleResource } from 'app/locale/selectors';

import {
  useLazyGetBetSearchResultQuery, useLazyGetPaymentSearchResultQuery, useLazyGetPlayerBonusSearchResultQuery, useLazyGetPlayerSearchResultQuery,
} from 'app/quickSearch/api';
import { FieldAccess } from 'app/layout/types/header';
import { getZIndexes } from 'utils/styledHelpers';
import { resource, types } from 'constants/accessControl';
import { title } from 'constants/tabs/title';
import {
  Bet, Brand, Payment, Player, PlayerBonus,
} from 'app/quickSearch/types';
import { onlyNumbersFormatter } from 'utils/utils';
import {
  StyledDrawerWrapper,
  StyledSearchDetails,
  StyledProviderName,
  StyledBrandName,
  StyledSearchWrapper,
  StyledProviderDetails,
  StyledProviderType,
  StyledProviderDot,
  StyledPlayerDetails,
} from 'app/layout/components/Layout.styled';
import { Current } from 'app/userPreferences/types';
import { StyledIconDefault } from 'shared/icon/Icon.styled';
import { Can } from 'shared/permission';
/* setInLocalStorage is used when there is brandId in header, and you want to change it after receiving fastSearch result, but don't clear filters.
 set LocalStorageService.get('isFromFastSearch') check before query reset in brand specific reports which we can redirect from fast search */

export interface SearchFieldsProps {
  visible: boolean;
  changeModalState: (state: boolean) => void;
  fieldAccesses: FieldAccess;
}

interface QuickSearchExtra {
  isOneMatch?: boolean;
  setInLocalStorage?: boolean;
}

interface AutoCompleteOption {
  value: string;
  label: ReactNode;
  key: number;
}

interface SearchField<T, R = Record<string, string>> {
  label: string;
  field: string;
  hide?: boolean;
  isNumeric?: boolean;
  showAsFiltered?: boolean;
  setInLocalStorage?: boolean;
  data?: T[];
  getSearchResult: (args: R) => void;
  getUrl: (data: T & QuickSearchExtra) => string;
  generateRow: (data: T & QuickSearchExtra, getUrl: (d: T & QuickSearchExtra) => string) => ReactNode;
}

type AnySearchField =
  | SearchField<Player & Brand>
  | SearchField<Payment & Brand, {paymentRequestId: string}>
  | SearchField<Bet & Brand>
  | SearchField<PlayerBonus & Brand, {playerBonusId: string}>;

type Field = 'userName' | 'firstName' | 'lastName' | 'email'

type Prefix = {
  [K in `${Field}Prefix`]?: string;
}

type SearchEntity = (Payment | Player | Bet | PlayerBonus) & Brand & QuickSearchExtra;

type Redirection = (data: SearchEntity, getUrlFn?: (item: SearchEntity) => string) => void;

type RowGenerator = (data: SearchEntity, getUrlFn?: (item: SearchEntity) => string) => ReactNode;

const bonusV2TypeMapping = {
  [enumTypes.campaignV2BonusFamilies.Combined]: title.campaignV2Bonuses.combined,
  [enumTypes.campaignV2BonusFamilies.FreeBet]: title.campaignV2Bonuses.freeBet,
  [enumTypes.campaignV2BonusFamilies.Special]: title.campaignV2Bonuses.special,
  [enumTypes.campaignV2BonusFamilies.Spin]: title.campaignV2Bonuses.spin,
};

const bonusTypeMapping = {
  [enumTypes.bonuses.Special]: title.bonuses.special,
  [enumTypes.bonuses.Spin]: title.bonuses.spins,
  [enumTypes.bonuses.Sport]: title.bonuses.sport,
  [enumTypes.bonuses.Casino]: title.bonuses.casino,
};

const selectLocale = makeSelectLocaleResource({ quickSearchResponseItemType: enumTypes.quickSearchResponseItemType, allBonusTypes: enumTypes.allBonusTypes, campaignV2BonusTypes: enumTypes.campaignV2Bonuses });

const { quickSearchZIndex } = getZIndexes();

const SearchFields = ({ visible, changeModalState, fieldAccesses }:SearchFieldsProps) => {
  const locale = useAppSelector(selectLocale);

  const { quickSearchResponseItemType, campaignV2BonusTypes, allBonusTypes } = locale;
  const { getDateTz, convertDateObjectToDateString } = useTimeZone();
  const [getBetSearchResult, { data: betSearchResult }] = useLazyGetBetSearchResultQuery();
  const [getPaymentSearchResult, { data: paymentSearchResult }] = useLazyGetPaymentSearchResultQuery();
  const [getPlayerBonusSearchResult, { data: playerBonusSearchResult }] = useLazyGetPlayerBonusSearchResultQuery();
  const [getPlayerSearchResult, { data: playerSearchResult }] = useLazyGetPlayerSearchResultQuery();

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const referenceCount = 1;

  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    setDrawerVisible(visible);
  }, [visible]);

  const keyCodes = {
    up: 38,
    down: 40,
  };

  const redirect = useCallback(<T, >(data: T & QuickSearchExtra & Brand, getUrl: (d: T & QuickSearchExtra) => string) => {
    const currentData = LocalStorageService.get('current') || {};

    LocalStorageService.set('current', {
      ...currentData,
      brandId: data.brandId,
    });

    if (data.setInLocalStorage && !data.isOneMatch) {
      LocalStorageService.set('isFromFastSearch', true);
    }

    NavigationService(getUrl(data));
  }, []);

  useEffect(
    () => () => {
      const { brandId } = LocalStorageService.get<Current>('current') || {};
      dispatch(setCurrentBrandId(brandId));
    },
    [dispatch],
  );

  const generatePlayerRow = useCallback(
    (data: Player & Brand & QuickSearchExtra, getUrl: (d: Player & Brand & QuickSearchExtra) => string) => {
      const {
        id, firstName, lastName, brandName,
      } = data;

      return (
        <div onClick={() => redirect(data, getUrl)}>
          <StyledSearchWrapper>
            <StyledSearchDetails $isMarginBottom>
              <div>{id}</div>
              <StyledBrandName>{brandName}</StyledBrandName>
            </StyledSearchDetails>
            <StyledPlayerDetails>{`${firstName || '-'} ${lastName || '-'}`}</StyledPlayerDetails>
          </StyledSearchWrapper>
        </div>
      );
    },
    [redirect],
  );

  const generatePaymentRow = useCallback(
    (data: Payment & Brand & QuickSearchExtra, getUrl: (d: Payment & Brand & QuickSearchExtra) => string) => {
      const { id, brandName, type } = data;

      return (
        <div onClick={() => redirect(data, getUrl)}>
          <StyledSearchWrapper>
            <StyledSearchDetails>
              <div>{id}</div>
              <StyledBrandName>{brandName}</StyledBrandName>
            </StyledSearchDetails>
            <StyledProviderDetails>
              <StyledProviderType>{quickSearchResponseItemType[type]}</StyledProviderType>
            </StyledProviderDetails>
          </StyledSearchWrapper>
        </div>
      );
    },
    [quickSearchResponseItemType, redirect],
  );

  const generateBetRow = useCallback(
    (data: Bet & Brand & QuickSearchExtra, getUrl: (d: Bet & Brand & QuickSearchExtra) => string) => {
      const {
        id, type, brandName, gameProviderName,
      } = data;

      return (
        <div onClick={() => redirect(data, getUrl)} key={type}>
          <StyledSearchWrapper>
            <StyledSearchDetails>
              <div>{id}</div>
              <StyledBrandName>{brandName}</StyledBrandName>
            </StyledSearchDetails>
            <StyledProviderDetails>
              <StyledProviderName>{gameProviderName}</StyledProviderName>
              {quickSearchResponseItemType[type] && (
                <>
                  <StyledProviderDot />
                  <StyledProviderType>{quickSearchResponseItemType[type]}</StyledProviderType>
                </>
              )}
            </StyledProviderDetails>
          </StyledSearchWrapper>
        </div>
      );
    },
    [quickSearchResponseItemType, redirect],
  );

  const generatePlayerBonusRow = useCallback(
    (data: PlayerBonus & Brand & QuickSearchExtra, getUrl: (d: PlayerBonus & Brand & QuickSearchExtra) => string) => {
      const { playerId, brandName, bonusType } = data;

      return (
        <div onClick={() => redirect(data, getUrl)} key={data.bonusType}>
          <StyledSearchWrapper>
            <StyledSearchDetails $isMarginBottom>
              <Flex vertical gap={8}>
                <StyledProviderName>{data.isNewBonusVersionEnabled ? campaignV2BonusTypes[bonusType] : allBonusTypes[bonusType]}</StyledProviderName>
                <strong>{playerId}</strong>
              </Flex>
              <StyledBrandName>{brandName}</StyledBrandName>
            </StyledSearchDetails>
          </StyledSearchWrapper>
        </div>
      );
    },
    [allBonusTypes, campaignV2BonusTypes, redirect],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchFields = useMemo<AnySearchField[]>(
    () => [
      {
        label: 'playerId',
        field: 'playerId',
        hide: !fieldAccesses.playerId,
        isNumeric: true,
        getSearchResult: getPlayerSearchResult,
        getUrl: (data: Player & QuickSearchExtra) => `/${data.isTestPlayer ? 'testPlayers' : 'players'}/${data.id}`,
        generateRow: generatePlayerRow,
        data: playerSearchResult,
      },
      {
        label: 'username',
        field: 'userName',
        hide: !fieldAccesses.username,
        showAsFiltered: true,
        getSearchResult: getPlayerSearchResult,
        getUrl: (data: Player & QuickSearchExtra & Prefix) => (data.isOneMatch ? `/players/${data.id}` : `/players?userName=${data.userNamePrefix}&expand=true`),
        generateRow: generatePlayerRow,
        setInLocalStorage: true,
        data: playerSearchResult,
      },
      {
        label: 'firstName',
        field: 'firstName',
        hide: !fieldAccesses.firstName,
        showAsFiltered: true,
        getSearchResult: getPlayerSearchResult,
        getUrl: (data: Player & QuickSearchExtra & Prefix) => (data.isOneMatch ? `/players/${data.id}` : `/players?firstName=${data.firstNamePrefix}&expand=true`),
        generateRow: generatePlayerRow,
        setInLocalStorage: true,
        data: playerSearchResult,
      },
      {
        label: 'lastName',
        field: 'lastName',
        hide: !fieldAccesses.lastName,
        showAsFiltered: true,
        getSearchResult: getPlayerSearchResult,
        getUrl: (data: Player & QuickSearchExtra & Prefix) => (data.isOneMatch ? `/players/${data.id}` : `/players?lastName=${data.lastNamePrefix}&expand=true`),
        generateRow: generatePlayerRow,
        setInLocalStorage: true,
        data: playerSearchResult,
      },
      {
        label: 'documentNumber',
        field: 'documentNumber',
        hide: !fieldAccesses.documentNumber,
        getSearchResult: getPlayerSearchResult,
        getUrl: (data: Player & QuickSearchExtra): string => `/players/${data.id}`,
        generateRow: generatePlayerRow,
        data: playerSearchResult,
      },
      {
        label: 'mobileNumber',
        field: 'mobileNumber',
        hide: !fieldAccesses.mobileNumber,
        getSearchResult: getPlayerSearchResult,
        getUrl: (data: Player & QuickSearchExtra) => `/players/${data.id}`,
        generateRow: generatePlayerRow,
        data: playerSearchResult,
      },
      {
        label: 'email',
        field: 'email',
        hide: !fieldAccesses.email,
        showAsFiltered: true,
        getSearchResult: getPlayerSearchResult,
        getUrl: (data: Player & QuickSearchExtra & Prefix) => (data.isOneMatch ? `/players/${data.id}` : `/players?email=${data.emailPrefix}&expand=true`),
        generateRow: generatePlayerRow,
        setInLocalStorage: true,
        data: playerSearchResult,
      },
      {
        label: 'betId',
        field: 'id',
        isNumeric: true,
        hide: !fieldAccesses.betId,
        getSearchResult: getBetSearchResult,
        getUrl: (data: Bet & Brand & QuickSearchExtra) => {
          const isCasino = enumTypes.quickSearchResponseItemType.CasinoBet === data.type;
          const isPlayer = Can({
            type: types.view,
            rule: [`${resource.bets}.${resource.casinoBets}`, `${resource.bets}.${resource.sportBets}`],
            children: false,
            fallback: true,
          });
          if (isPlayer) {
            return `/players/${data.playerId}?tab=${title.player.bets}&type=${isCasino ? title.player.casino : title.player.sport}&${getUrlSearchParams({ brandId: data.brandId, betId: data.id })}`;
          }
          return `/bets/${isCasino ? 'casinoTxnDetails' : 'sportTxnDetails'}/${data.brandId}/${data.id}?tab=${
            isCasino ? title.bets.casino : title.bets.sport
          }&createdDateFrom=${convertDateObjectToDateString(getDateTz().subtract(3, 'months').startOf('day'))}`;
        },
        generateRow: generateBetRow,
        data: betSearchResult,
      },
      {
        label: 'playerBonusId',
        field: 'playerBonusId',
        isNumeric: true,
        hide: !fieldAccesses.playerBonusId,
        showAsFiltered: true,
        getSearchResult: getPlayerBonusSearchResult,
        getUrl: (data: PlayerBonus & QuickSearchExtra) => {
          const tab = title.player.bonuses;
          const type = data.isNewBonusVersionEnabled ? bonusV2TypeMapping[data.bonusFamily] : bonusTypeMapping[data.bonusFamily];
          const idKey = data.isNewBonusVersionEnabled ? 'playerBonusId' : 'bonusPlayerId';
          return `/players/${data.playerId}?tab=${tab}&type=${type}&expand=true&${idKey}=${data.id}`;
        },
        generateRow: generatePlayerBonusRow,
        data: playerBonusSearchResult,
      },
      {
        label: 'roundIdCouponNumber',
        field: 'roundId',
        hide: !fieldAccesses.roundId,
        getSearchResult: getBetSearchResult,
        getUrl: (data: Bet & Brand & QuickSearchExtra) => {
          const isCasino = enumTypes.quickSearchResponseItemType.CasinoBet === data.type;
          const isPlayer = Can({
            type: types.view,
            rule: [isCasino ? `${resource.bets}.${resource.casinoBets}` : `${resource.bets}.${resource.sportBets}`],
            children: false,
            fallback: true,
          });
          if (isPlayer) {
            return `/players/${data.playerId}?tab=${title.player.bets}&type=${isCasino ? title.player.casino : title.player.sport}&${getUrlSearchParams({ brandId: data.brandId, betId: data.id })}`;
          }
          return `/bets/${isCasino ? 'casinoTxnDetails' : 'sportTxnDetails'}/${data.brandId}/${data.id}?tab=${
            isCasino ? title.bets.casino : title.bets.sport
          }&createdDateFrom=${convertDateObjectToDateString(getDateTz().subtract(3, 'months').startOf('day'))}`;
        },
        generateRow: generateBetRow,
        data: betSearchResult,
      },
      {
        label: 'paymentRequestId',
        field: 'paymentRequestId',
        isNumeric: true,
        hide: !fieldAccesses.paymentRequestId,
        getSearchResult: getPaymentSearchResult,
        getUrl: (data: Payment & Brand & QuickSearchExtra) => {
          const isDeposit = data.type === enumTypes.quickSearchResponseItemType.Deposit;
          const isPlayer = Can({
            type: types.view,
            rule: [isDeposit ? `${resource.financials}.${resource.deposits}` : `${resource.financials}.${resource.withdrawals}`],
            children: false,
            fallback: true,
          });
          if (isPlayer) {
            return `/players/${data.playerId}?tab=${title.player.payments}&type=${isDeposit ? title.player.deposits : title.player.withdrawals}&transactionId=${
              data.id
            }&createdDateFrom=${convertDateObjectToDateString(getDateTz().subtract(6, 'months').startOf('day'))}`;
          }
          return `/${isDeposit ? 'deposits' : 'withdrawals'}?transactionId=${data.id}&${qs.stringify({ createdDate: convertDateArrayToJSON([getDateTz().subtract(6, 'months').startOf('day'), null]) })}`;
        },
        generateRow: generatePaymentRow,
        data: paymentSearchResult,
      },
    ],
    [
      betSearchResult,
      convertDateObjectToDateString,
      fieldAccesses.betId,
      fieldAccesses.documentNumber,
      fieldAccesses.email,
      fieldAccesses.firstName,
      fieldAccesses.lastName,
      fieldAccesses.mobileNumber,
      fieldAccesses.paymentRequestId,
      fieldAccesses.playerBonusId,
      fieldAccesses.playerId,
      fieldAccesses.roundId,
      fieldAccesses.username,
      generateBetRow,
      generatePaymentRow,
      generatePlayerBonusRow,
      generatePlayerRow,
      getBetSearchResult,
      getDateTz,
      getPaymentSearchResult,
      getPlayerBonusSearchResult,
      getPlayerSearchResult,
      paymentSearchResult,
      playerBonusSearchResult,
      playerSearchResult,
    ],
  );

  const filteredFields = searchFields.filter((el) => !el.hide);

  const autoFocusInst = useRef<Record<string, InputRef | null>>({});
  const fieldsValue: Record<string, string> = {};
  filteredFields.forEach(({ field }) => {
    fieldsValue[field] = '';
  });
  const [values, setValues] = useState<Record<string, string| null>>(fieldsValue);
  const [options, setOptions] = useState<Record<string, AutoCompleteOption[]>>({});
  const [open, setOpen] = useState('');
  const [focus, setFocus] = useState('');
  const [current, setCurrent] = useState<AnySearchField | null>(null);

  const handleClose = useCallback(() => {
    setOptions({});
    changeModalState(false);
    setCurrent(null);
  }, [changeModalState]);

  const handleSelect = () => handleClose();

  const handleSearch = (field: string) => {
    const value = values[field];
    if (value && value.trim()) {
      // clear inputs
      let tempCurrent: AnySearchField | null = null;
      const newValues: Record<string, string> = {};
      filteredFields.forEach((el) => {
        if (field === el.field) {
          newValues[el.field] = value;
          tempCurrent = el;
          // search by field
          (tempCurrent?.getSearchResult as (args: Record<string, string>) => void)({ [field]: value });
          setCurrent(tempCurrent);
        } else {
          newValues[el.field] = '';
        }
      });
      setValues(newValues);
      // clear last options
      setOptions({});
    }
  };

  const handleChange = (field: string, value: string) => {
    const index = filteredFields.findIndex((el) => el.field === field);
    return setValues((prevValues) => ({
      ...prevValues,
      [field]: filteredFields[index].isNumeric ? onlyNumbersFormatter({ target: { value: value || '' } }) : value,
    }));
  };

  const handleVisibleChange = (isOpen: boolean) => {
    if (isOpen) {
      const firstFocus = filteredFields[0].field;
      setFocus(firstFocus);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { keyCode } = e;
    if (Object.values(keyCodes).includes(keyCode)) {
      let index = filteredFields.findIndex((el) => el.field === focus);
      if (e.keyCode === keyCodes.up && index) {
        index -= 1;
      } else if (e.keyCode === keyCodes.down && index < filteredFields.length - 1) {
        index += 1;
      }
      setFocus(filteredFields[index].field);
    }
  };

  useEffect(() => {
    if (focus) {
      autoFocusInst.current[focus]?.focus();
    }
  }, [focus]);

  useEffect(() => {
    if (!visible) {
      handleClose();
    }
  }, [visible, handleClose]);

  const searchResult = useMemo(() => searchFields.find((el) => el.field === current?.field)?.data, [current?.field, searchFields]);

  useEffect(() => {
    if (current?.field === focus && !_.isUndefined(searchResult)) {
      let newOptions: AutoCompleteOption[] = [];
      if (searchResult.length) {
        if (searchResult.length === referenceCount || (current?.showAsFiltered && searchResult.every(({ brandId }) => brandId && brandId === searchResult[0].brandId))) {
          handleClose();
          return (redirect as Redirection)(
            {
              ...searchResult[0],
              id: searchResult[0]?.id ?? values[current.field] ?? 0,
              [`${current?.field}Prefix`]: values[current?.field],
              isOneMatch: searchResult.length === referenceCount,
            },
            current?.getUrl as (item: SearchEntity) => string,
          );
        }
        newOptions = searchResult.map((el, index) => ({
          value: el.id.toString(),
          label: (current?.generateRow as RowGenerator)(
            {
              ...el,
              id: el.id ?? Number(values[current?.field]),
              isOneMatch: true,
              [`${current?.field}Prefix`]: values[current?.field],
            },
            current?.getUrl as (item: SearchEntity) => string,
          ),
          key: index,
        }));
      }
      setOptions({
        [current?.field]: newOptions,
      });
      setOpen(current?.field);
    }
  }, [focus, values, current, searchResult, handleClose, redirect]);

  const doRender = () => filteredFields.map(({ label, field }) => (
    <div key={field}>
      <div>{t(label)}</div>
      <AutoComplete
        notFoundContent={t('noResults')}
        onChange={(value) => handleChange(field, value)}
        value={values[field]}
        allowClear
        options={options[field] || []}
        onSelect={handleSelect}
        open={open === field}
      >
        <Input
          ref={(el) => {
            autoFocusInst.current[field] = el;
          }}
          suffix={(
            <StyledIconDefault onClick={() => handleSearch(field)}>
              <IconSvg icon="SearchIcon" width="1.14rem" height="1.14rem" />
            </StyledIconDefault>
          )}
          onPressEnter={() => handleSearch(field)}
          size="large"
          placeholder="Type"
          onBlur={() => setOpen('')}
          onFocus={() => {
            setFocus(field);
            return options[field] ? setOpen(field) : null;
          }}
          onKeyDown={handleKeyDown}
        />
      </AutoComplete>
    </div>
  ));

  return (
    <StyledDrawerWrapper
      closeIcon={(
        <StyledIconDefault>
          <IconSvg icon="CloseModalIcon" width="1.14rem" height="1.14rem" />
        </StyledIconDefault>
      )}
      placement="right"
      key="right"
      onClose={handleClose}
      open={drawerVisible}
      styles={{
        wrapper: {
          width: '32rem',
        },
      }}
      afterOpenChange={handleVisibleChange}
      zIndex={quickSearchZIndex}
    >
      {doRender()}
    </StyledDrawerWrapper>
  );
};

export default SearchFields;
